/**
 * @engine/core - Post-process pass
 *
 * The final PostProcess-stage pass (game viewport + player). Resolves the HDR scene offscreen target through
 * the {@link PostProcessStack} (bloom / tonemap / grading …) to the LDR backbuffer. This is the last step that
 * lived *outside* the scriptable pipeline — hand-called after `pipeline.execute()` in `GameViewRenderer.onRender`,
 * so it had no Profiler marker. As a {@link ViewportPass} it now (a) runs inside the executor, giving a free
 * `Render.PostProcess` marker in the same tree as every other pass, and (b) becomes scriptable — present only in
 * the game pipeline, absent from the editor (which renders straight to the backbuffer).
 *
 * The stack + scene target are renderer-owned resources (created in `onInitialize`, resized in place), so they're
 * injected as getters rather than captured once — the pass stays valid across the renderer's lifetime and doesn't
 * assume construction happens after initialization.
 *
 * @module @engine/core
 */

import type { PostProcessStack, PostProcessEffect, OffscreenTarget } from '@engine/render2d';
import { Profiler } from '../../Profiler';
import { ViewportPass, RenderStage, type ViewportPassContext } from '../RenderPipeline';

/**
 * Resolve the HDR scene target through the post-process stack → backbuffer.
 *
 * Runs whenever an offscreen scene target was bound this frame. With user effects it resolves the chain as
 * before; with zero effects but an HDR camera it still runs — the builtin `finalTonemap` (injected by the
 * renderer, owned outside the stack) becomes a single fullscreen tonemap pass, which is the always-tonemap
 * HDR contract. No-ops only when the scene went straight to the backbuffer (camera HDR off, no effects).
 * Owns the pre-sample `flushCompute()` — WebGPU must submit the queued scene work before the stack samples
 * that target as a texture (§7.2 of the SRP design).
 *
 * Per-effect timing: passes a profiling hook into `stack.render` so each effect nests a
 * `Render.PostProcess.<type>` marker under this pass's `Render.PostProcess`. The hook keeps the Profiler in
 * @engine/core — render2d (lower-level, no core dependency) only ever calls the opaque callback.
 */
export class PostProcessPass extends ViewportPass {
  readonly stage = RenderStage.PostProcess;
  readonly profileName = 'Render.PostProcess';

  constructor(
    private readonly getStack: () => PostProcessStack | null,
    private readonly getSceneTarget: () => OffscreenTarget | null,
    private readonly getFinalTonemap: () => PostProcessEffect | null = () => null,
  ) {
    super();
  }

  execute(c: ViewportPassContext): void {
    const stack = this.getStack();
    if (!stack) return;
    const sceneTarget = this.getSceneTarget();
    if (c.colorTarget === null || !sceneTarget) return; // no offscreen was bound this frame

    const finalTonemap = this.getFinalTonemap();
    if (!stack.hasActiveEffects && !finalTonemap) return; // legacy LDR path — nothing to resolve

    // Submit queued scene work before sampling the render target as a texture (WebGPU).
    if (c.ctx.flushCompute) c.ctx.flushCompute();

    stack.render(
      sceneTarget.getTexture(),
      null, // final destination = LDR backbuffer
      c.camera,
      c.time,
      c.deltaTime,
      (label, fn) => Profiler.measure(label, fn),
      { finalTonemap },
    );
  }
}
